import { FC, useEffect, useMemo, useState } from 'react';
import bbox from '@turf/bbox';
import { atom, useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { Layer, Source, type MapRef } from 'react-map-gl';

import { SkeletonPlaceholder } from '@carbon/react';

import MapboxMap from 'components/map/map';
import { FormattedCount } from 'components/formatted-count';
import { mapColors } from 'components/map/map-colors';
import { buildPolygonStyle } from 'lib/map';

import {
  focusedAssetIdAtom,
  focusedSlotIdAtom,
  packageMapConfigAtom,
  wizardMapDataAtom,
  wizardMapSelectedOnlyDataAtom,
} from './atoms';
import type { DraftMapSlotFeature } from './types';
import styles from './package-wizard-map.module.scss';

/** Optional Step 2 banner overlay: source label + asset/slot counts. */
export interface PackageWizardMapBanner {
  sourceName: string;
  assetCount: number;
  slotCount: number;
}

export interface PackageWizardMapProps {
  /**
   * Active draft id. When null, no features fetch fires and the map
   * renders an empty default-bounds view (Step 0 before draft creation).
   */
  draftId: string | null;
  banner?: PackageWizardMapBanner;
  /**
   * When `true`, render only the selected universe (`maps/features`) —
   * Step 1 table-side rows are ignored. Used by Step 2 (Summary) where
   * the user is reviewing a final selection; Step 1 leaves this off so
   * unselected rows can be previewed before selecting.
   */
  selectedOnly?: boolean;
}

const HIGHLIGHT_LINE_COLOR = 'yellow';
// Match well-page `LINE_STYLES` (`components/map/map-styles.ts`): wellbore
// lines render at 1.5px regardless of focus. Pin signal is the color flip
// to `HIGHLIGHT_LINE_COLOR`.
const SLOT_LINE_WIDTH = 1.5;
// Match well-page surface/bottom hole circle precedent (constant radius,
// no stroke) — see `components/map/well.module.scss` + the show page's
// `ScopedPackageWellsV2Layer`.
const HOLE_CIRCLE_RADIUS = 4;

const DEFAULT_CENTER = { longitude: -99.25, latitude: 31.25 };
const DEFAULT_ZOOM = 5;
// Sentinel so `focusedId` is always a string — Mapbox's `==` expression
// throws on null. Real ids never collide with this.
const NO_FOCUS = '__nofocus__';

// Match the visual contrast of `ScopedPackageDevelopmentAreasLayer` /
// `ScopedPackageAssetsLayer` so the wizard's draft-scoped polygons read as
// "scoped" against the basemap the same way they do on the package show
// page. `.line` is the at-rest paint; `.lineHighlighted` is the focus paint.
const DA_STYLE = buildPolygonStyle({
  color: mapColors.scopedUnitsFill,
  lineColor: mapColors.scopedUnitsBorder,
});
const ASSET_STYLE = buildPolygonStyle({
  color: mapColors.scopedAssetsFill,
  lineColor: mapColors.scopedAssetsBorder,
});

// Subscribe only to the wells-options sub-tree of the map config — the
// full atom updates on every basemap / unrelated layer change, which
// would otherwise re-render this map and bust the `collections` memo.
const wellsOptionsAtom = selectAtom(
  packageMapConfigAtom,
  (config) => config.wells.options
);

/**
 * Pull all non-null geometries off a slot (5 fields: stick, survey, eps,
 * surfaceHole, bottomHole). Used both for camera fit and for cataloguing
 * which categories to render.
 *
 * @param {DraftMapSlotFeature} s - A slot feature
 * @return {GeoJSON.Geometry[]} The slot's non-null geometries
 */
const slotGeometries = (s: DraftMapSlotFeature): GeoJSON.Geometry[] =>
  [
    s.survey,
    s.stick,
    s.estimatedProducingSegment,
    s.surfaceHole,
    s.bottomHole,
  ].filter((g): g is GeoJSON.LineString | GeoJSON.Point => g !== null);

/**
 * Build a Mapbox-shaped FeatureCollection from items + accessors. Skips
 * items whose geometry is null. The `id` is set both as the feature id
 * (so `['id']` paint expressions work) and on `properties` (for any
 * `['get', 'id']` consumers).
 *
 * @param {T[]} items - Source items to bundle
 * @param {Function} getId - Item → string id
 * @param {Function} getGeometry - Item → geometry or null
 * @return {GeoJSON.FeatureCollection} Collection ready for `<Source data>`
 */
const toCollection = <T,>(
  items: T[],
  getId: (item: T) => string,
  getGeometry: (item: T) => GeoJSON.Geometry | null
): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: items.flatMap((item) => {
    const geometry = getGeometry(item);
    if (!geometry) return [];
    const id = getId(item);
    return [{ type: 'Feature' as const, id, geometry, properties: { id } }];
  }),
});

/**
 * Compute a Mapbox-shaped `[[swLng, swLat], [neLng, neLat]]` bounding box
 * over a list of GeoJSON geometries. Returns null on empty input or when
 * `@turf/bbox` returns Infinity sentinels.
 *
 * @param {GeoJSON.Geometry[]} geometries - Geometries to bound
 * @return {[[number, number], [number, number]] | null} Bounds or null
 */
const computeBounds = (
  geometries: GeoJSON.Geometry[]
): [[number, number], [number, number]] | null => {
  if (geometries.length === 0) return null;
  const [minLng, minLat, maxLng, maxLat] = bbox({
    type: 'FeatureCollection',
    features: geometries.map((geometry) => ({
      type: 'Feature' as const,
      geometry,
      properties: null,
    })),
  });
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

/**
 * Step 1 + Step 2 wizard map. Renders the BE's draft features as one
 * batched Mapbox source per geometry category, so source count stays low
 * even with hundreds of slots and camera animations stay smooth.
 *
 * Camera fits to all features on initial load and on draft change;
 * pinning an asset or slot zooms in on that entity.
 *
 * @param {PackageWizardMapProps} props - draft id + optional Step 2 banner
 * @return {JSX.Element} The wizard map
 */
export const PackageWizardMap: FC<PackageWizardMapProps> = ({
  draftId,
  banner,
  selectedOnly = false,
}) => {
  const focusedAssetId = useAtomValue(focusedAssetIdAtom);
  const focusedSlotId = useAtomValue(focusedSlotIdAtom);
  // Single derived view-model — merged geometry + isLoading. `selectedOnly`
  // (Step 2) reads the slimmer atom that skips the table-side merge.
  const { assets, slots, developmentAreas, features, isLoading } = useAtomValue(
    selectedOnly ? wizardMapSelectedOnlyDataAtom : wizardMapDataAtom
  );
  const wellOptions = useAtomValue(wellsOptionsAtom);
  const [refAtom] = useState(() => atom<MapRef | null>(null));
  const mapRef = useAtomValue(refAtom);

  const collections = useMemo(
    () => ({
      assets: {
        type: 'FeatureCollection' as const,
        features: assets.flatMap((a) => {
          if (!a.geometry) return [];
          return [
            {
              type: 'Feature' as const,
              id: a.id,
              geometry: a.geometry,
              properties: { id: a.id, selected: a.selected },
            },
          ];
        }),
      },
      developmentAreas: toCollection(
        developmentAreas,
        (d) => d.name,
        (d) => d.geometry
      ),
      survey: toCollection(
        slots,
        (s) => s.id,
        (s) => (wellOptions.survey ? s.survey : null)
      ),
      // Survey takes precedence over stick — a slot's stick is emitted
      // only when its survey is missing.
      stick: toCollection(
        slots,
        (s) => s.id,
        (s) => (wellOptions.stick && s.survey === null ? s.stick : null)
      ),
      eps: toCollection(
        slots,
        (s) => s.id,
        (s) => s.estimatedProducingSegment
      ),
      surfaceHole: toCollection(
        slots,
        (s) => s.id,
        (s) => (wellOptions.surfaceHole ? s.surfaceHole : null)
      ),
      bottomHole: toCollection(
        slots,
        (s) => s.id,
        (s) => (wellOptions.bottomHole ? s.bottomHole : null)
      ),
    }),
    [assets, slots, developmentAreas, wellOptions]
  );

  // Bounds computed inline (not memoized) since they're used only at fit
  // time, gated by the deps below.
  useEffect(() => {
    if (!mapRef || !features) return;
    let geoms: GeoJSON.Geometry[];
    if (focusedAssetId !== null) {
      // Focus may target unselected rows too — find in the merged set,
      // not just `features`.
      const a = assets.find((x) => x.id === focusedAssetId);
      geoms = a?.geometry ? [a.geometry] : [];
    } else if (focusedSlotId !== null) {
      const s = slots.find((x) => x.id === focusedSlotId);
      geoms = s ? slotGeometries(s) : [];
    } else {
      // Unfocused fit tracks selection only — `maps/features` carries
      // the selected universe across all pages, regardless of any
      // table-side filter. `features` reference changes on every
      // refetch (mount + asset PATCH + slot PATCH), so each
      // selection-driven refresh refits the camera.
      geoms = [
        ...features.assets.flatMap((a) => (a.geometry ? [a.geometry] : [])),
        ...features.developmentAreas.flatMap((d) => {
          return d.geometry ? [d.geometry] : [];
        }),
        ...features.slots.flatMap(slotGeometries),
      ];
    }
    const bounds = computeBounds(geoms);
    if (!bounds) return;
    const isFocused = focusedAssetId !== null || focusedSlotId !== null;
    mapRef.getMap().fitBounds(bounds, {
      padding: isFocused ? 80 : 40,
      duration: 800,
    });
    // `assets` / `slots` read from closure; deps below gate refits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, draftId, focusedAssetId, focusedSlotId, features]);

  const focusedId = focusedAssetId ?? focusedSlotId ?? NO_FOCUS;

  // Memoized to keep paint references stable across unrelated re-renders
  // — react-map-gl shallow-diffs `paint` and re-runs `setPaintProperty`
  // on every fresh reference. Each value is a Mapbox `case` expression:
  // the feature whose `['id']` matches `focusedId` paints with the
  // highlighted value; everything else paints with the base.
  const paint = useMemo(
    () => ({
      // Asset paint mirrors the show-page precedent's `line` / `lineHighlighted`
      // pair: at rest = scoped border @ 0.4 opacity, focused = yellow @ 1.
      // Selected vs unselected reads off `['get', 'selected']` (set in the
      // wizard merge atom) to flip both fill and border colors.
      assetFillColor: [
        'case',
        ['get', 'selected'],
        mapColors.scopedAssetsFill,
        mapColors.scopedAssetsUnselectedFill,
      ] as mapboxgl.Expression,
      assetFillOpacity: [
        'case',
        ['==', ['id'], focusedId],
        1,
        0.3,
      ] as mapboxgl.Expression,
      assetLineOpacity: [
        'case',
        ['==', ['id'], focusedId],
        1,
        0.4,
      ] as mapboxgl.Expression,
      assetLineColor: [
        'case',
        ['==', ['id'], focusedId],
        HIGHLIGHT_LINE_COLOR,
        ['get', 'selected'],
        mapColors.scopedAssetsBorder,
        mapColors.scopedAssetsUnselectedBorder,
      ] as mapboxgl.Expression,
      // Slot wellbore lines — match well-page `LINE_STYLES.BLACK_SOLID` for
      // survey, `BLUE_DOTTED` for stick. Constant width; pin signal is the
      // color flip to yellow.
      surveyLineColor: [
        'case',
        ['==', ['id'], focusedId],
        HIGHLIGHT_LINE_COLOR,
        mapColors.verticalFill,
      ] as mapboxgl.Expression,
      stickLineColor: [
        'case',
        ['==', ['id'], focusedId],
        HIGHLIGHT_LINE_COLOR,
        mapColors.stickFill,
      ] as mapboxgl.Expression,
      epsLineColor: [
        'case',
        ['==', ['id'], focusedId],
        HIGHLIGHT_LINE_COLOR,
        mapColors.scopedHorizontalPerFill,
      ] as mapboxgl.Expression,
    }),
    [focusedId]
  );

  // Skeleton while any source feeding the merged set is in flight, or
  // until the canonical features payload has landed. Matches the
  // well-page / coverage-threshold skeleton precedent.
  if (isLoading) {
    return (
      <SkeletonPlaceholder
        data-testid="wizard-map-skeleton"
        className={styles.mapSkeleton}
      />
    );
  }

  return (
    <MapboxMap
      longitude={DEFAULT_CENTER.longitude}
      latitude={DEFAULT_CENTER.latitude}
      zoom={DEFAULT_ZOOM}
      mapConfigAtom={packageMapConfigAtom}
      refAtom={refAtom}
      showControls
      showMapFilters
    >
      <Source id="wizard-da" type="geojson" data={collections.developmentAreas}>
        <Layer id="wizard-da-fill" type="fill" paint={DA_STYLE.fill} />
        <Layer id="wizard-da-line" type="line" paint={DA_STYLE.line} />
      </Source>

      <Source id="wizard-assets" type="geojson" data={collections.assets}>
        <Layer
          id="wizard-assets-fill"
          type="fill"
          paint={{
            'fill-color': paint.assetFillColor,
            'fill-opacity': paint.assetFillOpacity,
          }}
        />
        <Layer
          id="wizard-assets-line"
          type="line"
          paint={{
            'line-color': paint.assetLineColor,
            'line-width': ASSET_STYLE.line['line-width'],
            'line-opacity': paint.assetLineOpacity,
          }}
        />
      </Source>

      <Source id="wizard-survey" type="geojson" data={collections.survey}>
        <Layer
          id="wizard-survey-line"
          type="line"
          paint={{
            'line-color': paint.surveyLineColor,
            'line-width': SLOT_LINE_WIDTH,
          }}
        />
      </Source>

      <Source id="wizard-stick" type="geojson" data={collections.stick}>
        <Layer
          id="wizard-stick-line"
          type="line"
          paint={{
            'line-color': paint.stickLineColor,
            'line-width': SLOT_LINE_WIDTH,
            'line-dasharray': [6, 2],
          }}
        />
      </Source>

      <Source id="wizard-eps" type="geojson" data={collections.eps}>
        <Layer
          id="wizard-eps-line"
          type="line"
          paint={{
            'line-color': paint.epsLineColor,
            'line-width': SLOT_LINE_WIDTH,
          }}
        />
      </Source>

      <Source
        id="wizard-surface-hole"
        type="geojson"
        data={collections.surfaceHole}
      >
        <Layer
          id="wizard-surface-hole-circle"
          type="circle"
          paint={{
            'circle-color': mapColors.surfaceHoleFill,
            'circle-radius': HOLE_CIRCLE_RADIUS,
          }}
        />
      </Source>

      <Source
        id="wizard-bottom-hole"
        type="geojson"
        data={collections.bottomHole}
      >
        <Layer
          id="wizard-bottom-hole-circle"
          type="circle"
          paint={{
            'circle-color': mapColors.bottomHoleFill,
            'circle-radius': HOLE_CIRCLE_RADIUS,
          }}
        />
      </Source>

      {banner && (
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>{banner.sourceName}</div>
          <div className={styles.bannerStat}>
            <FormattedCount value={banner.assetCount} />
            <span>Assets</span>
          </div>
          <div className={styles.bannerStat}>
            <FormattedCount value={banner.slotCount} />
            <span>Slots</span>
          </div>
        </div>
      )}
    </MapboxMap>
  );
};
